import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getAssignableRoles,
  getUserActivity,
  getUserPermissions,
  updateUserPermissions,
  updateUserRole,
  type AssignableRole,
  type UserActivity,
  type UserPermissions,
} from "../../services/userPermissions";
import {
  getUsers,
  removeUser,
  userDisplayName,
  userInitials,
  type OrganizationUser,
} from "../../services/users";
import { useAuth } from "../../hooks/useAuth";
import { Switch } from "../../components/Switch";
import { Button } from "../../components/Button";
import { ConfirmModal } from "../../components/ConfirmModal";
import { SelectPopover } from "../../components/SelectPopover";
import { ArrowLeftIcon } from "../../assets/ArrowLeftIcon";
import { roleLabel, formatUserDate } from "./userDisplay";
import { MESSAGING_ENABLED } from "../../constants/features";
import { PERMISSIONS } from "../../utils/permissions";
import "./UserDetailPage.css";

/**
 * One person's account: role, page access, and what they have been doing.
 *
 * Its own route rather than a panel under the table. A user has enough to say — two panels and a
 * role change behind a confirmation — that sharing the screen with the list left both cramped, and
 * a route means an admin can link someone straight to the person in question.
 */

const ACTIVITY_LABELS: Record<string, string> = {
  prospect: "Added player",
  note: "Wrote note",
  call: "Logged call",
  event: "Created event",
};

export const UserDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const signedInId = user?.profile?.id;

  const [person, setPerson] = useState<OrganizationUser | null>(null);
  const [roles, setRoles] = useState<AssignableRole[]>([]);
  const [access, setAccess] = useState<UserPermissions | null>(null);
  const [activity, setActivity] = useState<UserActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingRole, setPendingRole] = useState<string | null>(null);
  const [changingRole, setChangingRole] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);

  const loadDetail = useCallback(
    (userId: string) => {
      setError(null);
      // All at once: the header needs the person, the panels need the other two, and three
      // staggered spinners on one screen reads as something being broken.
      return Promise.all([
        getUsers(),
        getAssignableRoles(),
        getUserPermissions(userId),
        getUserActivity(userId),
      ])
        .then(([list, assignable, permissions, log]) => {
          const found = list.find((u) => u.id === userId) ?? null;
          // There is no by-id endpoint, so absence from the organization's list is what "not in
          // this organization" looks like from here.
          if (!found) {
            setNotFound(true);
            return;
          }
          setPerson(found);
          setRoles(assignable);
          setAccess(permissions);
          setActivity(log);
        })
        .catch(() => setError("Could not load this person's details."))
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => {
    if (!id) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    loadDetail(id);
  }, [id, loadDetail]);

  const handleToggle = async (key: string, allowed: boolean) => {
    if (!access) return;

    const next = access.permissions.map((p) =>
      p.key === key ? { ...p, allowed } : p
    );

    // Optimistic, then reconciled with the server's response. On failure the previous state is
    // restored rather than left showing a change that was never saved.
    const previous = access;
    setAccess({ ...access, permissions: next });
    setSaving(key);
    setError(null);

    try {
      const saved = await updateUserPermissions(
        access.userId,
        next.filter((p) => p.allowed).map((p) => p.key)
      );
      setAccess(saved);
    } catch {
      setAccess(previous);
      setError("That change was not saved. Please try again.");
    } finally {
      setSaving(null);
    }
  };

  const handleRoleConfirm = async () => {
    if (!access || !pendingRole || !id) return;

    setChangingRole(true);
    setError(null);

    try {
      await updateUserRole(access.userId, pendingRole);
      setPendingRole(null);
      // Re-read rather than patch: a promotion clears every page restriction, so the switches
      // would otherwise show an administrator wearing a coach's old access.
      await loadDetail(id);
    } catch (e) {
      // The API refuses to demote the last administrator, to touch a platform account, and to
      // change your own role. Its message says which, so it is shown rather than replaced.
      setError(
        e instanceof Error && e.message
          ? e.message
          : "That role change was not saved."
      );
      setPendingRole(null);
    } finally {
      setChangingRole(false);
    }
  };

  const handleRemoveConfirm = async () => {
    if (!person) return;

    setRemoving(true);
    setError(null);

    try {
      await removeUser(person.id);
      // Back to the list, which is the only honest place to be: this page is about someone who is
      // no longer in the organization.
      navigate("/settings/users");
    } catch (e) {
      /*
        The server refuses two cases with a 409 and says which in its message: your own account,
        and the organization's only administrator. Showing that message rather than replacing it is
        what makes the difference between "you cannot do that" and knowing what to do instead. The
        service supplies its own wording only when the server sent none.
      */
      // Only here: the success path has navigated away, and setting state on the route it just
      // left is a claim about a component that no longer exists.
      setRemoving(false);
      setConfirmRemove(false);
      setError(
        e instanceof Error && e.message
          ? e.message
          : "That person could not be removed."
      );
    }
  };

  if (loading) {
    return <p className="user-detail__muted">Loading…</p>;
  }

  if (notFound || !person || !access) {
    return (
      <div className="user-detail">
        <p className="user-detail__muted">
          That person is not in your organization.
        </p>
        <Button variant="secondary" onClick={() => navigate("/settings/users")}>
          Back to users
        </Button>
      </div>
    );
  }

  /*
    Messaging is dropped from the list while the feature itself is switched off, so an admin is not
    offered control over a page nobody can reach. Filtered for DISPLAY only — `access.permissions`
    keeps every entry, because saving sends the whole allowed list and dropping messaging from it
    would silently restrict a page the admin was never shown.

    Temporary, and tied to the same flag as the feature: when MESSAGING_ENABLED goes true this
    reverts to showing everything with no further change. See src/constants/features.ts.
  */
  const visiblePermissions = access.permissions.filter(
    (p) => MESSAGING_ENABLED || p.key !== PERMISSIONS.messaging
  );

  const allowedCount = visiblePermissions.filter((p) => p.allowed).length;
  const pending = roles.find((r) => r.key === pendingRole);
  // Neither a platform account nor your own account is this screen's to change.
  const canChangeRole =
    person.role !== "system_admin" && person.id !== signedInId && roles.length > 0;
  /*
    Same two exclusions, for the same reason one step further along: the server refuses both, and
    offering a control that can only be refused is how an admin learns the rule by being told no.
    A sole administrator removing themselves would leave nobody able to invite, set access, or
    receive anything the platform sends.
  */
  const canRemove = person.role !== "system_admin" && person.id !== signedInId;

  return (
    <div className="user-detail">
      <Link to="/settings/users" className="user-detail__back">
        <ArrowLeftIcon />
        Users
      </Link>

      {error && (
        <div className="user-detail__error" role="alert">
          {error}
        </div>
      )}

      <header className="user-detail__header">
        <span className="user-detail__avatar" aria-hidden="true">
          {userInitials(person)}
        </span>
        <div className="user-detail__identity">
          <h2 className="user-detail__name" id="user-detail-name">
            {userDisplayName(person)}
            {person.id === signedInId && (
              <span className="user-detail__you">You</span>
            )}
          </h2>
          <span className="user-detail__email">{person.email}</span>
        </div>
        <div className="user-detail__header-meta">
          <span
            className="user-detail__role"
            data-testid="user-detail-role"
          >
            {roleLabel(person.role)}
          </span>
          {canChangeRole && (
            <Button
              variant="secondary"
              onClick={() => setPendingRole(person.role)}
            >
              Change role
            </Button>
          )}
        </div>
      </header>

      <dl className="user-detail__facts">
        <div>
          <dt>Last active</dt>
          <dd>{formatUserDate(person.lastLoginAt)}</dd>
        </div>
        <div>
          <dt>Added</dt>
          <dd>{formatUserDate(person.createdAt)}</dd>
        </div>
        <div>
          <dt>Page access</dt>
          <dd>
            {access.isRestrictable
              ? `${allowedCount} of ${visiblePermissions.length}`
              : "All pages"}
          </dd>
        </div>
      </dl>

      <div className="user-detail__panels">
        <section className="user-detail__panel">
          {!access.isRestrictable ? (
            <>
              <div className="user-detail__panel-header">
                <h3 className="user-detail__panel-title">Page access</h3>
              </div>
              <p className="user-detail__note">
                Organization admins have access to every page, and can manage
                staff access and roles. Change their role to a coach if you need
                to limit what they can see.
              </p>
            </>
          ) : (
            <>
              <div className="user-detail__panel-header">
                <h3 className="user-detail__panel-title" id="user-detail-pages">
                  Page access
                </h3>
                <span className="user-detail__count">
                  {allowedCount} of {visiblePermissions.length}
                </span>
              </div>
              {/*
                role="group" + aria-labelledby rather than a <label> around the set: a label naming
                a group of controls is not a label, and trips label-has-associated-control
                (see web/CLAUDE.md).
              */}
              <ul
                className="user-detail__permissions"
                role="group"
                aria-labelledby="user-detail-pages"
              >
                {visiblePermissions.map((permission) => (
                  <li key={permission.key} className="user-detail__permission">
                    <Switch
                      className="user-detail__permission-switch"
                      checked={permission.allowed}
                      disabled={saving !== null}
                      onChange={(e) =>
                        handleToggle(permission.key, e.target.checked)
                      }
                      label={permission.label}
                    />
                    {/*
                      Reserved space rather than a conditional element, so a row does not reflow
                      every time a save starts and finishes.
                    */}
                    <span className="user-detail__saving">
                      {saving === permission.key ? "Saving…" : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="user-detail__panel">
          <div className="user-detail__panel-header">
            <h3 className="user-detail__panel-title">Activity</h3>
          </div>
          {activity && (
            <>
              <ul className="user-detail__stats">
                <li>
                  <span className="user-detail__stat-value">
                    {activity.counts.prospects}
                  </span>
                  <span className="user-detail__stat-label">Players</span>
                </li>
                <li>
                  <span className="user-detail__stat-value">
                    {activity.counts.notes}
                  </span>
                  <span className="user-detail__stat-label">Notes</span>
                </li>
                <li>
                  <span className="user-detail__stat-value">
                    {activity.counts.calls}
                  </span>
                  <span className="user-detail__stat-label">Calls</span>
                </li>
                <li>
                  <span className="user-detail__stat-value">
                    {activity.counts.events}
                  </span>
                  <span className="user-detail__stat-label">Events</span>
                </li>
              </ul>

              {activity.recent.length === 0 ? (
                <p className="user-detail__muted user-detail__muted--inline">
                  Nothing recorded yet.
                </p>
              ) : (
                <ul className="user-detail__activity">
                  {activity.recent.map((item, index) => (
                    <li
                      key={`${item.type}-${item.occurredAt}-${index}`}
                      className="user-detail__activity-item"
                    >
                      <span className="user-detail__activity-what">
                        {ACTIVITY_LABELS[item.type] ?? "Activity"}
                        {item.description ? `: ${item.description}` : ""}
                      </span>
                      <span className="user-detail__activity-when">
                        {formatUserDate(item.occurredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/*
                Stated rather than implied. This is built from the records a coach created, so an
                admin must not read a short list as "they did nothing" when creations are all it
                ever showed.
              */}
              <p className="user-detail__caveat">
                Shows players, notes, calls and events this person created. Edits
                and sign-ins are not recorded.
              </p>
            </>
          )}
        </section>
      </div>

      {canRemove && (
        <section className="user-detail__panel user-detail__danger">
          <div className="user-detail__panel-header">
            <h3 className="user-detail__panel-title">
              Remove from organization
            </h3>
          </div>
          <p className="user-detail__note">
            They lose access straight away and stop appearing in your users
            list. The players, notes, calls and events they added stay where
            they are.
          </p>
          <Button
            variant="secondary"
            className="user-detail__remove"
            onClick={() => setConfirmRemove(true)}
          >
            Remove {userDisplayName(person)}
          </Button>
        </section>
      )}

      <ConfirmModal
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={handleRemoveConfirm}
        busy={removing}
        title="Remove from organization"
        message={`Remove ${userDisplayName(person)} from your organization? They lose access straight away. You can invite them again later, and they would set a new password.`}
        confirmText={removing ? "Removing…" : "Yes, remove them"}
      />

      <ConfirmModal
        isOpen={pendingRole !== null}
        onClose={() => setPendingRole(null)}
        onConfirm={handleRoleConfirm}
        busy={changingRole}
        title="Change role"
        message={
          pending?.key === "admin"
            ? `Make ${userDisplayName(person)} an organization admin? They will get access to every page and be able to manage staff access and roles.`
            : `Make ${userDisplayName(person)} a coach? You will then choose which pages they can open.`
        }
        confirmText={changingRole ? "Saving…" : "Yes, change role"}
      >
        <div className="user-detail__role-picker">
          <span className="user-detail__role-picker-label">Role</span>
          {/*
            Opens on the role they currently hold. Defaulting to the opposite one showed a value the
            admin had not chosen, and made the message above describe a change they had not asked
            for yet.
          */}
          <SelectPopover
            options={roles.map((r) => r.label)}
            value={pending?.label ?? ""}
            onChange={(label) =>
              setPendingRole(roles.find((r) => r.label === label)?.key ?? null)
            }
            placeholder="Select a role"
            clearable={false}
            fullWidth
          />
          {pending && (
            <p className="user-detail__role-picker-description">
              {pending.description}
            </p>
          )}
        </div>
      </ConfirmModal>
    </div>
  );
};
